import React, { useCallback, useEffect, useState } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
	ArrowLeft,
	MessageSquare,
	Image as ImageIcon,
	StickyNote,
	Star,
	Megaphone,
	CalendarClock,
	Briefcase,
	UserPlus,
	CircleDot,
	Loader2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import CustomerMemoryPanel from "./CustomerMemoryPanel";
import { touchContact } from "../../services/recentContext";

// Cada tipo de evento tem ícone e cor próprios — a timeline é lida de relance.
const TIPOS = {
	ticket: { icone: MessageSquare, classe: "bg-primary/10 text-primary" },
	ticketClosed: {
		icone: CircleDot,
		classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
	},
	message: { icone: MessageSquare, classe: "bg-muted text-muted-foreground" },
	media: { icone: ImageIcon, classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
	note: { icone: StickyNote, classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
	rating: { icone: Star, classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
	campaign: { icone: Megaphone, classe: "bg-violet-500/10 text-violet-600 dark:text-violet-400" },
	scheduled: { icone: CalendarClock, classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400" },
	lead: { icone: UserPlus, classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
	deal: { icone: Briefcase, classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
};

const FILTROS = [
	{ value: "tudo", rotulo: "Tudo" },
	{ value: "conversa", rotulo: "Conversas", tipos: ["message", "ticket", "ticketClosed"] },
	{ value: "arquivos", rotulo: "Arquivos", tipos: ["media"] },
	{ value: "notas", rotulo: "Notas", tipos: ["note"] },
	{ value: "crm", rotulo: "CRM", tipos: ["lead", "deal"] },
	{ value: "campanhas", rotulo: "Campanhas", tipos: ["campaign", "scheduled"] },
];

const ContactTimeline = () => {
	const { contactId } = useParams();
	const navigate = useNavigate();

	const [dados, setDados] = useState(null);
	const [carregando, setCarregando] = useState(true);
	const [filtro, setFiltro] = useState("tudo");

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get(`/contacts/${contactId}/timeline`);
			setDados(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [contactId]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	const { contato, eventos = [], totais = {} } = dados || {};

	useEffect(() => {
		if (contato?.id) {
			touchContact(contato);
		}
	}, [contato?.id, contato?.name]);

	const filtroAtual = FILTROS.find((f) => f.value === filtro);

	const visiveis = filtroAtual?.tipos
		? eventos.filter((e) => filtroAtual.tipos.includes(e.tipo))
		: eventos;

	const abas = FILTROS.map((f) => ({
		...f,
		contador: f.tipos
			? f.tipos.reduce((soma, t) => soma + (totais[t] || 0), 0)
			: eventos.length,
	}));

	return (
		<MainContainer>
			<div className="flex items-center gap-2 border-b px-4 py-3 sm:px-6">
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 shrink-0"
					aria-label="Voltar"
					onClick={() => navigate(-1)}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">
						{contato?.name}
					</h1>
					<p className="truncate text-xs tabular-nums text-muted-foreground">
						{contato?.number} · toda a jornada com este cliente
					</p>
				</div>
			</div>

			<PageTabs abas={abas} atual={filtro} onChange={setFiltro} />

			{contactId && <CustomerMemoryPanel contactId={contactId} />}

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{visiveis.length === 0 ? (
					<p className="py-16 text-center text-sm text-muted-foreground">
						Nada registrado nesta categoria.
					</p>
				) : (
					<ol className="relative space-y-3 pl-6">
						{/* A linha vertical é o que dá o sentido de "linha do tempo". */}
						<span
							aria-hidden="true"
							className="absolute bottom-2 left-[11px] top-2 w-px bg-border"
						/>

						{visiveis.map((evento, indice) => {
							const tipo = TIPOS[evento.tipo] || TIPOS.message;
							const Icone = tipo.icone;

							return (
								<li key={indice} className="relative">
									<span
										className={cn(
											"absolute -left-6 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-background",
											tipo.classe
										)}
									>
										<Icone className="h-3 w-3" />
									</span>

									<div
										className={cn(
											"rounded-xl border bg-card p-3",
											evento.ticketId && "cursor-pointer hover:bg-muted/40"
										)}
										onClick={() =>
											evento.ticketId &&
											navigate(`/tickets/${evento.ticketId}`)
										}
									>
										<div className="flex flex-wrap items-baseline justify-between gap-2">
											<p className="text-sm font-medium">{evento.titulo}</p>
											<time className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
												{format(parseISO(evento.data), "dd/MM/yyyy HH:mm")}
											</time>
										</div>

										{evento.descricao && (
											<p className="mt-1 whitespace-pre-line wrap-break-word text-xs leading-relaxed text-muted-foreground">
												{evento.descricao.length > 300
													? `${evento.descricao.slice(0, 300)}…`
													: evento.descricao}
											</p>
										)}

										{evento.meta?.mediaUrl &&
											["image", "sticker"].includes(evento.meta.mediaType) && (
											<img
												src={evento.meta.mediaUrl}
												alt=""
												className="mt-2 h-24 w-32 rounded-lg object-cover"
											/>
										)}

										{(evento.autor || evento.ticketId) && (
											<p className="mt-1.5 text-[11px] text-muted-foreground">
												{evento.autor}
												{evento.autor && evento.ticketId && " · "}
												{evento.ticketId && `Atendimento #${evento.ticketId}`}
											</p>
										)}
									</div>
								</li>
							);
						})}
					</ol>
				)}
			</div>
		</MainContainer>
	);
};

export default ContactTimeline;
